import { useState, useEffect, useRef } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  isOffline: boolean;
  wasOffline: boolean;
}

export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const consecutiveFailuresRef = useRef(0);
  const isOnlineRef = useRef(isOnline);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Mark that we came back online (this will be used to show success toast)
      setWasOffline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Additional check with periodic ping to detect network issues
  useEffect(() => {
    const fetchWithTimeout = async (
      url: string,
      options: RequestInit & { method?: 'GET' | 'HEAD' } = {},
      timeoutMs = 6000,
    ) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        return response;
      } finally {
        clearTimeout(timer);
      }
    };

    const probeConnectivity = async () => {
      // Prefer same-origin small resources; external fallback last
      const origin = window.location.origin;
      const probes = [
        `${origin}/manifest.webmanifest`,
        `${origin}/favicon.ico`,
        // Minimal external probe (widely available), used only as a fallback
        'https://www.gstatic.com/generate_204',
      ];

      let success = false;

      for (const url of probes) {
        try {
          // Try HEAD first (cheap), then GET if HEAD not supported
          let resp = await fetchWithTimeout(url, { method: 'HEAD', cache: 'no-store' }, 5000);
          if (!resp.ok || resp.status === 405) {
            resp = await fetchWithTimeout(url, { method: 'GET', cache: 'no-store' }, 5000);
          }

          if (resp.ok || resp.status === 204) {
            success = true;
            break;
          }
        } catch {
          // Continue to next probe
        }
      }

      if (success) {
        consecutiveFailuresRef.current = 0;
        if (!isOnlineRef.current) {
          setIsOnline(true);
          setWasOffline(true); // mark transition for restored toast
        }
      } else {
        // Only mark offline after a couple of consecutive failures to avoid false positives
        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current >= 2) {
          setIsOnline(false);
          setWasOffline(true);
        }
      }
    };

    // Check connection every 30 seconds when online and page is visible
    const interval = setInterval(() => {
      if (navigator.onLine && isPageVisible) {
        probeConnectivity();
      }
    }, 30000);

    // Run an initial probe shortly after mount if online
    if (navigator.onLine) {
      setTimeout(() => {
        if (isPageVisible) {
          probeConnectivity();
        }
      }, 1500);
    }

    return () => clearInterval(interval);
  }, [isPageVisible]);

  const result = {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
  };

  return result;
};
